import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Loader2, RefreshCw, ArrowLeftRight, Baby, Receipt
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';

const formatMontant = (montant) => (
  `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`
);

const ICONES = {
  transfert_eleve: ArrowLeftRight,
  transfert_employe: ArrowLeftRight,
  enfant_a_charge: Baby,
  transaction_en_echec: Receipt
};

const FAMILLES = [
  { cle: 'transferts_eleves', titre: 'Transferts d\'élèves', unite: 'chaîne' },
  { cle: 'transferts_employes', titre: 'Transferts d\'employés', unite: 'entité' },
  { cle: 'enfants_a_charge', titre: 'Enfants à charge', unite: 'liaison' }
];

/**
 * Ce que le Hub affirme, confronté à ce que les sites contiennent réellement.
 *
 * L'écran existe parce qu'un transfert marqué « exécuté » pouvait désigner un
 * élève qui n'existe plus nulle part, et une liaison « active » retenir chaque
 * mois un montant sur un salaire pour créditer un enfant supprimé — sans que
 * rien, jamais, ne le signale.
 *
 * Le contrôle est en lecture seule : il constate, il ne répare pas. Les
 * corrections restent des décisions humaines.
 */
export default function CoherenceTransfertsPanel() {
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur('');
    try {
      const reponse = await api.intersite.coherenceTransferts();
      if (reponse?.success) setRapport(reponse.data);
      else setErreur(reponse?.error || 'Le contrôle n\'a pas pu aboutir.');
    } catch (e) {
      setErreur(e.message || 'Le contrôle n\'a pas pu aboutir.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const anomalies = rapport
    ? [
      ...(rapport.transferts_eleves?.anomalies || []),
      ...(rapport.transferts_employes?.anomalies || []),
      ...(rapport.enfants_a_charge?.anomalies || [])
    ]
    : [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="min-w-0">
          <h3 className="font-bold text-gray-900">Transferts et enfants à charge</h3>
          <p className="text-xs text-gray-500">
            Ce que le Hub affirme, confronté à ce que les sites contiennent vraiment.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={charger} disabled={loading}>
          {loading
            ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
          Relancer
        </Button>
      </div>

      {/* Premier passage : rien à montrer, on dit qu'on travaille. */}
      {loading && !rapport && (
        <LoadingSpinner message="Vérification de l'état des sites…" />
      )}

      {erreur && (
        <div className="m-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{erreur}</span>
        </div>
      )}

      {rapport && (
        <div className="p-4">
          {/* Ce qui a été regardé. Un « tout va bien » ne vaut que si l'on sait
              sur quoi il porte. */}
          <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {FAMILLES.map(({ cle, titre, unite }) => {
              const bloc = rapport[cle] || {};
              const nb = bloc.nb_verifies || 0;
              const pb = (bloc.anomalies || []).length;
              return (
                <div key={cle} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{titre}</p>
                  <p className="mt-1 text-sm text-gray-800">
                    <span className="font-bold">{nb}</span> {unite}{nb > 1 ? 's' : ''} vérifiée{nb > 1 ? 's' : ''}
                  </p>
                  <p className={`text-[11px] ${pb > 0 ? 'font-semibold text-red-700' : 'text-emerald-700'}`}>
                    {pb > 0 ? `${pb} anomalie${pb > 1 ? 's' : ''}` : 'aucune anomalie'}
                  </p>
                </div>
              );
            })}
          </div>

          {anomalies.length === 0 ? (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
              <div className="text-sm text-emerald-900">
                <span className="font-semibold">Tout concorde.</span>
                {' '}Chaque transfert exécuté trouve bien son dossier au site d'arrivée, et chaque
                liaison active désigne un élève et un employé existants.
              </div>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <div className="text-sm text-amber-900">
                  <span className="font-semibold">
                    {anomalies.length} anomalie{anomalies.length > 1 ? 's' : ''}
                    {rapport.nb_bloquants > 0 && `, dont ${rapport.nb_bloquants} bloquante${rapport.nb_bloquants > 1 ? 's' : ''}`}.
                  </span>
                  {' '}Ce contrôle ne corrige rien : il montre ce qui ne concorde pas, à vous de décider.
                </div>
              </div>

              <div className="space-y-2">
                {anomalies.map((a, index) => {
                  const Icone = ICONES[a.type] || AlertTriangle;
                  const bloquant = a.gravite === 'bloquant';
                  return (
                    <div
                      key={`${a.type}-${a.reference}-${index}`}
                      className={`rounded-lg border px-3 py-2.5 ${bloquant
                        ? 'border-red-200 bg-red-50'
                        : 'border-amber-200 bg-amber-50'}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${bloquant ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          <Icone className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">{a.libelle}</span>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${bloquant ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                              {bloquant ? 'Bloquant' : 'À vérifier'}
                            </span>
                            {a.montant_mensuel > 0 && (
                              <span className="text-[11px] font-medium text-gray-600">
                                {formatMontant(a.montant_mensuel)} / mois
                              </span>
                            )}
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {(a.constats || []).map((c, i) => (
                              <li key={i} className="text-[11px] leading-snug text-gray-700">• {c}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <p className="mt-3 text-[11px] text-gray-500">
            Contrôle effectué le{' '}
            {rapport.genere_le
              ? new Date(rapport.genere_le).toLocaleString('fr-FR')
              : '—'}
            . Aucune donnée n'a été modifiée.
          </p>
        </div>
      )}
    </div>
  );
}
